/**
    Courtesy: Emeke Ajeh (https://github.com/Godofbrowser)
    Source: https://github.com/Godofbrowser/axios-refresh-multiple-request/blob/master/src/script.js
*/

import axios, {
  AxiosError, AxiosRequestConfig, AxiosInstance, AxiosResponse,
} from 'axios';

interface RetryableAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

let isRefreshing = false;
let failedQueue: Array<{ resolve: (value: unknown) => void; reject: (reason?: any) => void }> = [];

const processQueue = (error: Error | null, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

const responseInterceptor = (axiosInstance: AxiosInstance) => async (error: AxiosError) => {
  const _axios = axiosInstance;
  const originalRequest: RetryableAxiosRequestConfig = error.config!!;
  const response: AxiosResponse = error.response!!;

  if (
    response
    && (response?.status === 401 || response?.status === 403)
    && !originalRequest._retry
  ) {
    if (isRefreshing) {
      try {
        const token = await new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        });

        originalRequest.headers!!.Authorization = `Bearer ${token}`;

        return _axios.request(originalRequest);
      } catch (err) {
        return Promise.reject(err);
      }
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem('api-refresh-token') || '';

    return new Promise((resolve, reject) => {
      axios('api.ENDPOINT.xyz', {
        method: 'post',
        headers: {
          /* Pass custom secrets here. One sample provided */
          'x-client-name': process.env.REACT_APP_AUTH_API_CLIENT_NAME!!,
        },
        data: {
          refreshToken,
        },
      })
        .then(({ data }) => {
          if (data.token && data.refreshToken) {
            localStorage.setItem('api-token', data.token);
            localStorage.setItem('api-refresh-token', data.refreshToken);
            _axios.defaults.headers.common.Authorization = `Bearer ${data.token}`;
            originalRequest.headers!!.Authorization = `Bearer ${data.token}`;
            processQueue(null, data.token);
            resolve(_axios(originalRequest));
          } else {
            localStorage.clear();
            window.location.href = `${window.location.origin}/auth/login`;
            throw new Error("Something's wrong while regenerating refreshToken");
          }
        })
        .catch((err) => {
          localStorage.clear();
          window.location.href = `${window.location.origin}/auth/login`;
          processQueue(err, null);
          reject(err);
          // Logout
        })
        .then(() => {
          isRefreshing = false;
        });
    });
  }

  return Promise.reject(
    new Error(
      'Network Error: Either there is no response from server or could not connect to server',
    ),
  );
};

export default responseInterceptor;
